import { useRef } from "react"
import { useFormContext } from "react-hook-form"

import { runWithGuardRef } from "@/utils/forms"
import { roundToPrecision } from "@/utils/number"

enum LotFormField {
  QuantityPerPackage = "QUANTITY_PER_PACKAGE",
  UnitCount = "UNITS_COUNT",
  Quantity = "QUANTITY",
}

interface LotFieldPaths {
  quantityPerPackage: string
  unitCount: string
  quantity: string
}

interface UseAdjustmentLotQuantityCalculationParams {
  fieldPaths: LotFieldPaths
}

interface UseAdjustmentLotQuantityCalculationReturn {
  handleQuantityPerPackageChange: (value: number | null) => void
  handleUnitCountChange: (value: number | null) => void
  handleQuantityChange: (value: number | null) => void
}

export const useAdjustmentLotQuantityCalculation = ({
  fieldPaths,
}: UseAdjustmentLotQuantityCalculationParams): UseAdjustmentLotQuantityCalculationReturn => {
  const { setValue, watch } = useFormContext()
  const isUpdatingRef = useRef<LotFormField | null>(null)

  const [quantityPerPackage, unitCount] = watch([fieldPaths.quantityPerPackage, fieldPaths.unitCount])

  const handleQuantityPerPackageChange = (value: number | null) => {
    if (isUpdatingRef.current !== null && isUpdatingRef.current !== LotFormField.QuantityPerPackage) return

    runWithGuardRef(
      () => {
        const newQuantity = unitCount === null || value === null ? null : roundToPrecision(unitCount * value, 2)
        setValue(fieldPaths.quantity, newQuantity, { shouldValidate: true })
      },
      isUpdatingRef,
      { activeValue: LotFormField.QuantityPerPackage, inactiveValue: null }
    )
  }

  const handleUnitCountChange = (value: number | null) => {
    if (isUpdatingRef.current !== null && isUpdatingRef.current !== LotFormField.UnitCount) return

    runWithGuardRef(
      () => {
        const newQuantity =
          value === null || quantityPerPackage === null ? null : roundToPrecision(value * quantityPerPackage, 2)
        setValue(fieldPaths.quantity, newQuantity, { shouldValidate: true })
      },
      isUpdatingRef,
      { activeValue: LotFormField.UnitCount, inactiveValue: null }
    )
  }

  const handleQuantityChange = (value: number | null) => {
    if (isUpdatingRef.current !== null && isUpdatingRef.current !== LotFormField.Quantity) return

    runWithGuardRef(
      () => {
        let newUnitCount: number | null

        if (value === null || quantityPerPackage === null) {
          newUnitCount = null
        } else {
          newUnitCount = quantityPerPackage === 0 ? 0 : roundToPrecision(value / quantityPerPackage, 2)
        }

        setValue(fieldPaths.unitCount, newUnitCount, { shouldValidate: true })
      },
      isUpdatingRef,
      { activeValue: LotFormField.Quantity, inactiveValue: null }
    )
  }

  return { handleQuantityPerPackageChange, handleUnitCountChange, handleQuantityChange }
}
